import { ItemTaxViewData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { actionWindowCommands } from '../../command-menu/menu-mapping';
import { EJRecordTypes } from '../../common/enumerations/ej-record-types';
import { validateWeight } from '../../common/helpers/weight-uom-validate';
import { IButton } from '../../common/interfaces/IButton';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { store } from '../../common/redux/configure-store';
import { MiddleAreaActions } from '../../common/redux/reducers/middle-area-reducer';
import { CalculationMethodType, TaxType } from './tax-rate.interface';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { ICommandConfiguration } from 'src/common/interfaces/dtos/ICommandsGroup';

export const getActionWindowButtons = (selectedItem: IElectronicJournalRecord): IButton[] => {
  const xlrConfig = getCurrentXLRConfig();
  const isTareWeightItem = selectedItem.TareWeight?.TareValue !== undefined;
  const isTBRActive =
    store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn;
  const stateFilterKey = (type: EJRecordTypes, uomCode?: string): string => {
    switch (type) {
      case EJRecordTypes.Tender:
        return 'TenderDetailsViewCommands';
      case EJRecordTypes.ReturnItem:
      case EJRecordTypes.Product:
        if (isTBRActive) {
          return 'ReturnTransactionItemsPostSaleCommands';
        } else if (
          validateWeight(uomCode) &&
          (xlrConfig.allowModifyTareForWeightedItems || isTareWeightItem)
        ) {
          return 'ItemWithTareCommands';
        } else {
          return 'ItemsPostSaleCommands';
        }
      case EJRecordTypes.Coupon:
        return 'CouponModeCommands';
      default:
        return 'ItemsPostSaleCommands';
    }
  };

  const currentStateFilterKey = stateFilterKey(selectedItem.Type, selectedItem.UnitOfMeasureCode);

  return store
    .getState()
    .posUiMenu.Focus.commands.filter(command =>
      command.states.find(state => state.state === currentStateFilterKey),
    )
    .sort((a, b) => parseInt(a.commandOrder, 10) - parseInt(b.commandOrder, 10))
    .map(command => {
      const commandConfiguration: ICommandConfiguration = {
        allowOverride: command.allowOverride,
        checkRole: command.checkRole,
        userHasPermission: command.userHasPermission,
        name: command.commandId,
      };

      return {
        ...actionWindowCommands[command.commandId],
        label: command.commandId,
        disabled: !command.userHasPermission && !command.allowOverride,
        command: commandConfiguration,
      };
    });
};

export const showActionWindowOfSelectedItem = (): void => {
  const selectedItem = store.getState().itemList.ejItems.find(item => item.Selected);
  if (selectedItem) {
    store.dispatch(MiddleAreaActions.showActionWindow());
  }
};

export const getItemFeeList = (items: IElectronicJournalRecord[]): IElectronicJournalRecord[] => {
  const results: IElectronicJournalRecord[] = [];
  items.forEach(item => {
    item?.Taxes?.forEach(tax => {
      if (hasTaxItemFee(tax)) {
        results.push(item);
      }
    });
  });
  return results;
};

export const getTransactionFeeList = (
  items: IElectronicJournalRecord[],
): IElectronicJournalRecord[] => {
  const results: IElectronicJournalRecord[] = [];
  items.forEach(item => {
    item?.Taxes?.forEach(tax => {
      if (hasTaxTransactionFee(tax)) {
        results.push(item);
      }
    });
  });
  return results;
};

export const hasTaxItemFee = (tax: Partial<ItemTaxViewData>): boolean => {
  const rates = store.getState().itemList.effectiveTaxRates;
  return rates.some(rate => rate.rateId === tax.code && rate.type === TaxType.ItemFee);
};

export const hasTaxTransactionFee = (tax: Partial<ItemTaxViewData>): boolean => {
  const rates = store.getState().itemList.effectiveTaxRates;
  return rates.some(
    rate =>
      rate.rateId === tax.code &&
      rate.type === TaxType.TransactionFee &&
      rate.calculationMethod.calculationMethodType !== CalculationMethodType.FixedAmount,
  );
};
